import fs from 'fs';
import path, {dirname} from 'path';
import {fileURLToPath} from 'url';

import {fileExists, fileExistsSync} from '@shopify/cli-kit/node/fs';
import {outputInfo, outputWarn} from '@shopify/cli-kit/node/output';
import {joinPath} from '@shopify/cli-kit/node/path';
import {AbortError} from '@shopify/cli-kit/node/error';

import {
  ClientError,
  DeploymentConfig,
  DeploymentToken,
} from '../deploy/types.js';

export const deployDefaults: Record<string, string | number> = {
  assetsDirDefault: 'dist/client/',
  buildCommandDefault: 'npm build',
  verificationDurationDefault: 180,
  maxUploadAttempts: 3,
  maxResumabeUploadAttempts: 9,
  workerDirDefault: 'dist/server/',
};

export function errorHandler(error: any) {
  if (isClientError(error)) {
    if (error.statusCode === 401) {
      throw new AbortError(
        'You are not authorized to perform this action. Please check your deployment token.',
      );
    }

    if (error.statusCode === 429) {
      throw new AbortError(
        "You've made too many requests. Please try again later",
      );
    }

    if ([502, 503, 504].includes(error.statusCode)) {
      if (error.message && error.message.trim().length > 0) {
        throw new AbortError(
          `${error.message}. Please try your deployment again.`,
        );
      }

      throw new AbortError(
        'The server is currently unavailable. Please try again later.',
      );
    }

    if (error.statusCode === 500) {
      if (error.message && error.message.trim().length > 0) {
        throw new AbortError(
          `${error.message}. Please try your deployment again.`,
        );
      }

      throw new AbortError(
        'An unknown error occurred, please try your deployment again',
      );
    }
  }

  if (error instanceof AbortError && error.message.includes('503')) {
    throw new AbortError(
      'The server is currently unavailable. Please try again later.',
    );
  }
}

export function getBuildCommandFromLockFile(config: DeploymentConfig): string {
  const lockFileBuildCommands = new Map([
    ['package-lock.json', 'npm run build'],
    ['pnpm-lock.yaml', 'pnpm run build'],
    ['yarn.lock', 'yarn build'],
  ]);

  const foundLockFiles = [];

  for (const [lockFileName, buildCommand] of lockFileBuildCommands) {
    if (fileExistsSync(joinPath(config.rootPath!, lockFileName))) {
      foundLockFiles.push({lockFileName, buildCommand});
    }
  }

  if (foundLockFiles.length > 1) {
    const lockFilesList = foundLockFiles
      .map(({lockFileName}) => lockFileName)
      .join(', ');
    outputWarn(
      `Warning: Multiple lock files found: (${lockFilesList}).`,
      stderrLogger,
    );
  }

  if (foundLockFiles.length > 0) {
    const {lockFileName, buildCommand} = foundLockFiles[0] as {
      lockFileName: string;
      buildCommand: string;
    };

    const infoMsg = foundLockFiles.length > 1 ? '' : `Found: ${lockFileName}. `;

    outputInfo(
      `${infoMsg}Assuming "${buildCommand}" as build command. Use the buildCommand flag to override.`,
      stderrLogger,
    );
    return buildCommand;
  }

  return String(deployDefaults.buildCommandDefault);
}

export enum Header {
  OxygenNamespaceHandle = 'X-Oxygen-Namespace-Handle',
}

export function isClientError(error: unknown): error is ClientError {
  return typeof error === 'object' && error !== null && 'statusCode' in error;
}

export function loadPackageJson(projectFolder?: string) {
  let jsonPath;
  if (projectFolder) {
    jsonPath = path.join(projectFolder, 'package.json');
  } else {
    const __filename = fileURLToPath(import.meta.url);
    const __dirname = dirname(__filename);
    jsonPath = path.join(__dirname, '../../package.json');
  }

  if (!fs.existsSync(jsonPath)) {
    return {};
  }
  const packageJsonData = fs.readFileSync(jsonPath, 'utf8');
  return JSON.parse(packageJsonData);
}

export function stderrLogger(log: string): void {
  process.stderr.write(`${log}\n`);
}

export const maxLabelLength = 375;

export function parseToken(inputToken: string): DeploymentToken {
  try {
    const decodedToken = Buffer.from(inputToken, 'base64').toString('utf-8');
    const rawToken = JSON.parse(decodedToken);
    return convertKeysToCamelCase(rawToken) as DeploymentToken;
  } catch (error) {
    throw new Error(
      `Error processing deployment token. Please check your token and try again.`,
    );
  }
}

export function authBypassTokenDurationParse(
  authBypassTokenDurationFlag?: string,
): number | undefined {
  if (!authBypassTokenDurationFlag) {
    return undefined;
  }
  const duration = Number(authBypassTokenDurationFlag);
  if (isNaN(duration)) {
    throw new AbortError(
      "Invalid duration format. Please specify the duration in hours (e.g., '10').",
    );
  }
  if (duration > 12 || duration < 1) {
    throw new AbortError(
      'Invalid duration length. Please specify the duration (in hours) between 1 to 12 hours.',
    );
  }
  return duration;
}

interface VerifyConfigParams {
  config: DeploymentConfig;
  performedBuild?: boolean;
}

export async function verifyConfig({
  config,
  performedBuild = false,
}: VerifyConfigParams) {
  const {rootPath, workerDir, assetsDir, skipBuild, workerOnly} = config;
  const checkPaths: Record<string, string> = {
    root: rootPath!,
  };

  if (skipBuild || performedBuild) {
    checkPaths.worker = joinPath(rootPath!, workerDir!);
    if (!workerOnly) {
      checkPaths.assets = joinPath(rootPath!, assetsDir!);
    }
  }

  for (const pathType of Object.keys(checkPaths)) {
    await checkPath(checkPaths[pathType]!, pathType);
  }

  const addressRegex =
    /^https:\/\/(?:[\w-]+\.)*[\w-]+|^https:\/\/\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}/;
  if (!addressRegex.test(config.deploymentUrl)) {
    throw new Error(`Invalid deployment service URL: ${config.deploymentUrl}`);
  }
}

async function checkPath(path: string, pathType: string) {
  if (!(await fileExists(path))) {
    if (pathType === 'assets') {
      outputWarn(
        `Use the "workerOnly" flag to perform a worker-only deployment.`,
        stderrLogger,
      );
    }
    throw new Error(`Path not found: ${path}`);
  }
}

interface TokenType {
  [x: string]: string | TokenType;
}

function convertKeysToCamelCase(
  obj: TokenType | string,
): DeploymentToken | string {
  if (typeof obj === 'object') {
    return Object.keys(obj).reduce((result: any, key: string) => {
      const camelCaseKey = key.replace(/([-_][a-z])/gi, ($1) =>
        $1.toUpperCase().replace('-', '').replace('_', ''),
      );
      if (obj[key] === undefined) {
        throw new Error(`Invalid token: ${key} is undefined`);
      }
      result[camelCaseKey] = convertKeysToCamelCase(obj[key] as TokenType);
      return result;
    }, {} as DeploymentToken);
  }
  return obj;
}
